import * as React from "react";
import { Button } from "@/components/ui/button";
import { Plus, BookOpen, ExternalLink } from "lucide-react";
import { KnowledgeBaseWithDocuments } from "../../../../types/navigationTypes";
import { ICON_SIZES } from "@/lib/icon-sizes";
import { PanelSectionHeader, PanelSearchField } from "@/taskpane/shared/components/panel-layout";
import { cn } from "@/lib/utils";

interface KnowledgeBaseHeaderProps {
  searchTerm: string;
  onSearchChange: (term: string) => void;
  knowledgeBases: KnowledgeBaseWithDocuments[];
  projectKBs: KnowledgeBaseWithDocuments[];
  sharedKBs: KnowledgeBaseWithDocuments[];
  activeTab: "project" | "shared";
  onTabChange: (tab: "project" | "shared") => void;
  onCreateKnowledgeBase: () => void;
  onUploadDocument: () => void;
  isReadOnly?: boolean;
}

const KnowledgeBaseHeader: React.FC<KnowledgeBaseHeaderProps> = ({
  searchTerm,
  onSearchChange,
  knowledgeBases,
  projectKBs,
  sharedKBs,
  activeTab,
  onTabChange,
  onCreateKnowledgeBase,
  onUploadDocument,
  isReadOnly = false,
}) => {
  const getAppUrl = () => {
    const deploymentMode = process.env.VITE_DEPLOYMENT_MODE;
    return deploymentMode === "b2c" ? "https://www.useworkops.com" : "https://www.workops.eu";
  };

  const settingsUrl = `${getAppUrl()}/settings/knowledge-bases`;

  const trailing =
    knowledgeBases.length > 0 ? (
      <div className="flex flex-wrap items-center gap-2">
        {isReadOnly && (
          <div className="flex items-center gap-2">
            <span className="text-xs text-slate-500">Read-only mode</span>
            <a
              href={settingsUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 text-xs font-medium text-primary hover:text-primary/80 hover:underline"
            >
              Manage in settings
              <ExternalLink size={12} />
            </a>
          </div>
        )}
        {!isReadOnly && (
          <>
            <Button variant="outline" onClick={() => onCreateKnowledgeBase()} size="sm" className="h-8 text-xs px-3">
              <BookOpen size={ICON_SIZES.sm} className="mr-1.5" />
              Knowledge base
            </Button>
            <Button onClick={onUploadDocument} size="sm" className="h-8 text-xs px-3">
              <Plus size={ICON_SIZES.sm} className="mr-1.5" />
              Upload
            </Button>
          </>
        )}
      </div>
    ) : undefined;

  return (
    <div className="mb-1">
      <PanelSectionHeader
        icon={BookOpen}
        title="Knowledge Bases"
        count={knowledgeBases.length > 0 ? knowledgeBases.length : undefined}
        countLabel="bases"
        trailing={trailing}
        className="mb-3"
      />

      <div className="mb-3 flex w-fit items-center gap-1 rounded-lg bg-slate-100/80 p-1">
        <button
          type="button"
          onClick={() => onTabChange("shared")}
          className={cn(
            "flex items-center gap-2 rounded-md px-3 py-1.5 text-xs font-medium transition-all",
            activeTab === "shared"
              ? "border border-slate-200 bg-white text-slate-900 shadow-sm"
              : "text-slate-600 hover:bg-slate-200/60 hover:text-slate-900"
          )}
        >
          Shared
          {sharedKBs.length > 0 && (
            <span
              className={cn(
                "ml-0.5 rounded-full px-1.5 py-0.5 text-[10px]",
                activeTab === "shared" ? "bg-primary/10 text-primary" : "bg-slate-200/80 text-slate-600"
              )}
            >
              {sharedKBs.length}
            </span>
          )}
        </button>
        <button
          type="button"
          onClick={() => onTabChange("project")}
          className={cn(
            "flex items-center gap-2 rounded-md px-3 py-1.5 text-xs font-medium transition-all",
            activeTab === "project"
              ? "border border-slate-200 bg-white text-slate-900 shadow-sm"
              : "text-slate-600 hover:bg-slate-200/60 hover:text-slate-900"
          )}
        >
          Project
          {projectKBs.length > 0 && (
            <span
              className={cn(
                "ml-0.5 rounded-full px-1.5 py-0.5 text-[10px]",
                activeTab === "project" ? "bg-primary/10 text-primary" : "bg-slate-200/80 text-slate-600"
              )}
            >
              {projectKBs.length}
            </span>
          )}
        </button>
      </div>

      <div className="mb-4 flex items-center gap-2">
        <PanelSearchField
          value={searchTerm}
          onChange={onSearchChange}
          placeholder="Search files and folders…"
          className="w-full"
        />
      </div>
    </div>
  );
};

export default KnowledgeBaseHeader;
